/**
 * AdminUsagePage - 消費者 AI チャットの利用統計
 *
 * 月間チャット利用数・日次推移・利用上位ユーザー、
 * および全ユーザー横断のチャット会話一覧。
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import adminService from '../../services/adminService';
import AdminLayout from './AdminLayout';
import TrendChart from '../../components/admin/TrendChart';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Content = styled.div`
  animation: ${fadeIn} 0.35s ease-out;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const StatCard = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
  padding: 20px;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  color: ${(p) => p.theme.colors.text};
  font-family: ${(p) => p.theme.fonts?.mono};
`;

const StatSub = styled.div`
  font-size: 0.74rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-top: 4px;
`;

const Section = styled.section`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
  padding: 22px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 4px 0;
`;

const SectionSub = styled.div`
  font-size: 0.76rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-bottom: 16px;
`;

const ConvItem = styled.button`
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 12px;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
  padding: 11px 4px;
  cursor: pointer;
  color: inherit;
  font-family: ${(p) => p.theme.fonts.main};

  &:last-child { border-bottom: none; }
  &:hover { background: ${(p) => p.theme.colors.bgTertiary}; }
`;

const ConvGlyph = styled.span`
  font-family: ${(p) => p.theme.fonts.display};
  font-size: 1.1rem;
  color: ${(p) => p.$accent || p.theme.colors.primary};
  width: 20px;
  text-align: center;
  flex-shrink: 0;
`;

const ConvMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConvTitle = styled.div`
  font-size: 0.86rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConvMeta = styled.div`
  font-size: 0.72rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin-top: 2px;
`;

const ConvCount = styled.div`
  font-size: 0.74rem;
  color: ${(p) => p.theme.colors.textMuted};
  flex-shrink: 0;
  font-family: ${(p) => p.theme.fonts?.mono};
`;

const EmptyNote = styled.div`
  font-size: 0.82rem;
  color: ${(p) => p.theme.colors.textMuted};
  padding: 18px 0;
  text-align: center;
`;

function AdminUsagePage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usageData, convData] = await Promise.all([
        adminService.getUsage(),
        adminService.getAllConversations(100).catch(() => ({ conversations: [] })),
      ]);
      setUsage(usageData);
      setConversations(convData?.conversations || []);
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (error) {
    return (
      <AdminLayout active="usage">
        <Content style={{ textAlign: 'center', padding: 64 }}>
          <p style={{ color: '#EF4444' }}>{error}</p>
        </Content>
      </AdminLayout>
    );
  }

  const trend = usage?.daily_trend || [];

  return (
    <AdminLayout active="usage">
      <Content>
        <StatsGrid>
          <StatCard>
            <StatLabel>{t('チャット利用 (今月)', 'Chat Usage (month)')}</StatLabel>
            <StatValue>{loading ? '--' : usage?.chat_usage || 0}</StatValue>
            <StatSub>{usage?.period || '--'}</StatSub>
          </StatCard>
          <StatCard>
            <StatLabel>{t('チャット会話数 (累計)', 'Total Conversations')}</StatLabel>
            <StatValue>{loading ? '--' : conversations.length}</StatValue>
            <StatSub>{t('直近 100 件まで表示', 'showing latest 100')}</StatSub>
          </StatCard>
          <StatCard>
            <StatLabel>{t('組織分析 (今月)', 'Org Analysis (month)')}</StatLabel>
            <StatValue>{loading ? '--' : usage?.org_analysis_usage || 0}</StatValue>
            <StatSub>{t('B2B 機能', 'B2B feature')}</StatSub>
          </StatCard>
        </StatsGrid>

        {/* 日次推移 */}
        <Section>
          <SectionTitle>{t('チャット利用の日次推移', 'Daily Chat Usage')}</SectionTitle>
          <SectionSub>{t('過去 14 日', 'Last 14 days')}</SectionSub>
          {loading ? (
            <EmptyNote>{t('読み込み中...', 'Loading...')}</EmptyNote>
          ) : trend.length ? (
            <TrendChart data={trend} />
          ) : (
            <EmptyNote>{t('データがありません', 'No data')}</EmptyNote>
          )}
        </Section>

        {/* 全ユーザー横断のチャット会話一覧 */}
        <Section>
          <SectionTitle>{t('最近のチャット会話 (全ユーザー)', 'Recent Conversations (all users)')}</SectionTitle>
          <SectionSub>{t('クリックでユーザー詳細へ。会話全文はそこで閲覧できます', 'Click to open the user detail page')}</SectionSub>
          {loading ? (
            <EmptyNote>{t('読み込み中...', 'Loading...')}</EmptyNote>
          ) : conversations.length ? (
            conversations.map((c) => (
              <ConvItem key={c.id} onClick={() => navigate(`/admin/users/${c.user_id}`)}>
                <ConvGlyph>✦</ConvGlyph>
                <ConvMain>
                  <ConvTitle>{c.title || t('(無題)', '(untitled)')}</ConvTitle>
                  <ConvMeta>
                    {c.user_name} ·{' '}
                    {c.updated_at ? new Date(c.updated_at).toLocaleString() : '--'}
                  </ConvMeta>
                </ConvMain>
                <ConvCount>{c.message_count || 0} {t('msg', 'msg')}</ConvCount>
              </ConvItem>
            ))
          ) : (
            <EmptyNote>{t('チャット会話がまだありません', 'No conversations yet')}</EmptyNote>
          )}
        </Section>
      </Content>
    </AdminLayout>
  );
}

export default AdminUsagePage;
